import { createContext, useContext, useState, useEffect, ReactNode } from 'react'
import { supabase } from '@/lib/supabase'

interface MaintenanceModeContextType {
  isMaintenanceMode: boolean
  loading: boolean
  setMaintenanceMode: (enabled: boolean) => Promise<boolean>
  refetch: () => Promise<void>
}

const MaintenanceModeContext = createContext<MaintenanceModeContextType | undefined>(undefined)

export function MaintenanceModeProvider({ children }: { children: ReactNode }) {
  const [isMaintenanceMode, setIsMaintenanceMode] = useState(false)
  const [loading, setLoading] = useState(true)

  const fetchMaintenanceMode = async () => {
    try {
      const { data, error } = await supabase
        .from('site_settings')
        .select('value')
        .eq('key', 'maintenance_mode')
        .single()

      if (error) {
        if (error.code === 'PGRST116') {
          setIsMaintenanceMode(false)
          setLoading(false)
          return
        }
        throw error
      }

      const value = data?.value
      setIsMaintenanceMode(value === true || value === 'true' || value === '1')
    } catch {
      setIsMaintenanceMode(false)
    } finally {
      setLoading(false)
    }
  }

  const setMaintenanceMode = async (enabled: boolean) => {
    try {
      const { error } = await supabase
        .from('site_settings')
        .upsert(
          {
            key: 'maintenance_mode',
            value: enabled ? 'true' : 'false',
            updated_at: new Date().toISOString(),
          },
          {
            onConflict: 'key',
          }
        )

      if (error) throw error

      setIsMaintenanceMode(enabled)
      return true
    } catch {
      return false
    }
  }

  // Only fetch once on mount - no polling
  useEffect(() => {
    fetchMaintenanceMode()
  }, [])

  return (
    <MaintenanceModeContext.Provider
      value={{
        isMaintenanceMode,
        loading,
        setMaintenanceMode,
        refetch: fetchMaintenanceMode,
      }}
    >
      {children}
    </MaintenanceModeContext.Provider>
  )
}

export function useMaintenanceMode() {
  const context = useContext(MaintenanceModeContext)
  if (context === undefined) {
    throw new Error('useMaintenanceMode must be used within a MaintenanceModeProvider')
  }
  return context
}

